import { UploadOutlined } from "@ant-design/icons";
import { Alert, Button, Divider, Empty, Modal, Space, Table, Upload, UploadFile, UploadProps } from "antd";
import { ColumnsType } from "antd/lib/table";
import axios from "axios";
import { CardTitleCsvImport, CardTitleCsvImportStatus, CardTitleCsvImportStatusNames } from "models/cardIngestion";
import { FC, useState } from "react";
import { localizedDateAndTime } from "util/helpers";
import useCardTitleCsvImport from "./useCardTitleCsvImport";

interface CardTitleCsvImportModalProps {
    open: boolean;
    onCancel: () => void;
}

const columns: ColumnsType<CardTitleCsvImport> = [
    {
        title: "Uploaded",
        dataIndex: "uploaded",
        render: localizedDateAndTime,
    },
    {
        title: "Filename",
        dataIndex: "filename",
    },
    {
        title: "Status",
        dataIndex: "status",
        render: (s: CardTitleCsvImportStatus) => CardTitleCsvImportStatusNames[s]
    }
];

const CardTitleCsvImportModal: FC<CardTitleCsvImportModalProps> = ({ open, onCancel }) => {
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const { history = [], uploadCsv, refetchHistory, isLoading, isUploading } = useCardTitleCsvImport();
    const [error, setError] = useState<string | null>(null);

    const rowExpandable = (rec: CardTitleCsvImport): boolean =>
        rec.status === CardTitleCsvImportStatus.Completed ||
        rec.status === CardTitleCsvImportStatus.Failed;

    const expandedRowRender = (rec: CardTitleCsvImport) => {
        if (rec.status === CardTitleCsvImportStatus.Completed) {
            return <span style={{ display: 'flex', justifyContent: 'end' }}>
                {rec.created} Created, {rec.skipped} Skipped
            </span>;
        } else if (rec.status === CardTitleCsvImportStatus.Failed) {
            return <span style={{ color: "var(--dcs-error-red)" }}>
                {rec.exception}
            </span>
        }

        return null;
    }

    const reset = () => {
        setFileList([]);
        setError(null);
        onCancel();
    }

    const uploadFile = async () => {
        try {
            setError(null);
            await uploadCsv(fileList[0]);
            await refetchHistory();
            reset();
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const err = error.response!.data as any;
                setError(`${err.title} - ${err.errors.workflow[0]}`);
            }
        }
    }

    const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
        setFileList(newFileList);
        setError(null);
    }

    return <Modal
        title="Import Card Title CSV"
        open={open}
        onCancel={reset}
        onOk={uploadFile}
        okButtonProps={{ disabled: fileList.length === 0 || isLoading || isUploading }}
        cancelButtonProps={{ disabled: isLoading || isUploading }}
        closable={false}
        keyboard={false}
        maskClosable={false}
        width={1000}
        destroyOnClose
    >
        <Space direction="vertical" split={<Divider />} style={{ width: '100%' }}>
            <Space direction="vertical">
                <Upload
                    accept=".csv"
                    maxCount={1}
                    beforeUpload={() => false}
                    customRequest={uploadFile}
                    listType="text"
                    fileList={fileList}
                    onChange={handleChange}
                >
                    <Button type="primary" icon={<UploadOutlined />}>Select CSV File</Button>
                </Upload>

                {error && <Alert
                    type="error"
                    message={error}
                    showIcon
                />}
            </Space>

            <Space direction="vertical" style={{ width: '100%' }}>
                <span style={{ fontWeight: 'bold' }}>Recent CSV Upload History</span>
                <Table
                    size="small"
                    columns={columns}
                    rowKey={r => r.id}
                    pagination={false}
                    locale={{ emptyText: <Empty description="No card title CSVs have been imported yet" /> }}
                    dataSource={history}
                    expandable={{
                        defaultExpandedRowKeys: history
                            .filter(x => x.status === CardTitleCsvImportStatus.Failed)
                            .map(x => x.id),
                        expandRowByClick: true,
                        expandedRowRender,
                        rowExpandable
                    }}
                />
            </Space>
        </Space>
    </Modal>;
}

export default CardTitleCsvImportModal;